import React, { useState, useRef, useCallback } from 'react';
import { B } from '../constants';
import { promos as promosAPI } from '../api';
import QualitySettings from './QualitySettings';
import BatchProgress from './BatchProgress';
import Input from './ui/Input';

const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
const MAX_SIZE = 100 * 1024 * 1024;
const MAX_FILES = 20;

export default function BatchUploadModal({ onClose, onSuccess, user, onAuthRequired }) {
  const [files, setFiles] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [qualitySettings, setQualitySettings] = useState({ preset: 'medium', codec: 'h264', customBitrate: null });
  const fileInputRef = useRef(null);

  const validateFile = useCallback((file) => {
    if (!ALLOWED_TYPES.includes(file.type)) return `Invalid type: ${file.type}`;
    if (file.size > MAX_SIZE) return `Too large: ${(file.size / 1024 / 1024).toFixed(1)}MB`;
    return null;
  }, []);

  const addFiles = useCallback((newFiles) => {
    setError('');
    const valid = [];
    for (const f of newFiles) {
      if (files.length + valid.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files`);
        break;
      }
      const err = validateFile(f);
      if (err) { setError(`${f.name}: ${err}`); continue; }
      const existingNames = new Set([...files, ...valid].map(x => x.name));
      if (existingNames.has(f.name)) continue;
      valid.push(f);
    }
    if (valid.length === 0) return;
    setFiles(prev => [...prev, ...valid.map(f => ({
      file: f,
      name: f.name,
      size: f.size,
      type: f.type,
      preview: URL.createObjectURL(f),
      title: f.name.replace(/\.[^/.]+$/, '').slice(0, 80),
      caption: '',
      destination: '',
    }))]);
  }, [files, validateFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = '';
  }, [addFiles]);

  const removeFile = useCallback((index) => {
    setFiles(prev => {
      const item = prev[index];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const updateFileMeta = useCallback((index, key, value) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, [key]: value } : f));
  }, []);

  const uploadFilesToS3 = async () => {
    const fileMetas = files.map(f => ({ contentType: f.type, size: f.size, name: f.name }));
    const { results } = await promosAPI.uploadBatch(fileMetas);
    const errors = results.filter(r => r.error);
    if (errors.length > 0) throw new Error(errors.map(e => `${e.name}: ${e.error}`).join('; '));

    const keys = {};
    const uploadPromises = results.map((r, i) => {
      const file = files[i];
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(prev => ({ ...prev, [i]: Math.round((e.loaded / e.total) * 100) }));
          }
        };
        xhr.onload = () => {
          setUploadProgress(prev => ({ ...prev, [i]: 100 }));
          if (xhr.status >= 200 && xhr.status < 300) {
            keys[r.name] = r.key;
            resolve();
          } else reject(new Error(`${r.name} upload failed (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error(`${r.name} upload failed`));
        xhr.open('PUT', r.uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file.file);
      });
    });

    await Promise.all(uploadPromises);
    return keys;
  };

  const handleSubmit = async () => {
    if (!user) { onAuthRequired(); return; }
    if (files.length === 0) { setError('Add at least one video'); return; }
    setCreating(true);
    setError('');
    try {
      setUploading(true);
      const keys = await uploadFilesToS3();
      setUploading(false);

      const promos = files.map(f => ({
        title: f.title,
        videoKey: keys[f.name],
        caption: f.caption || null,
        destination: f.destination || null,
        qualityPreset: qualitySettings.preset,
        codec: qualitySettings.codec,
        customBitrate: qualitySettings.customBitrate || null,
      }));

      const result = await promosAPI.createBatch(promos);
      setBatchId(result.batchId);
      setStep(3);
    } catch (err) {
      setError(err.message || 'Batch creation failed');
    } finally { setCreating(false); }
  };

  const handleClose = () => {
    files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    if (batchId && onSuccess) onSuccess({ batchId });
    else onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && handleClose()}>

      {batchId ? (
        <BatchProgress batchId={batchId} onClose={handleClose} />
      ) : (
        <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
          <div style={{ background: B.night, padding: '24px 28px', borderRadius: '24px 24px 0 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: 'white', margin: 0 }}>
                  📦 Batch Upload
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>
                  Upload up to {MAX_FILES} videos at once
                </p>
              </div>
              <span onClick={handleClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              {[1, 2].map(s => (
                <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: step >= s ? B.mint : 'rgba(255,255,255,0.2)' }} />
              ))}
            </div>
          </div>

          <div style={{ padding: 28 }}>
            {error && (
              <div style={{ background: 'rgba(220,53,69,0.08)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#dc3545', fontSize: 13, fontFamily: "'Montserrat',sans-serif" }}>
                ⚠️ {error}
              </div>
            )}

            {step === 1 ? (
              <>
                <div onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                  style={{ border: `2px dashed ${dragOver ? B.forest : '#ddd'}`, borderRadius: 12, padding: '32px 20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? 'rgba(26,107,74,0.05)' : '#fafafa', transition: 'all 0.2s', marginBottom: 16 }}
                  onClick={() => fileInputRef.current?.click()}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📹</div>
                  <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 600, color: B.night, marginBottom: 4 }}>Drop videos here or click to browse</p>
                  <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: B.muted }}>MP4, MOV, WebM, AVI — max 100MB each, up to {MAX_FILES} files</p>
                </div>
                <input ref={fileInputRef} type="file" multiple accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                  onChange={handleFileSelect} style={{ display: 'none' }} />

                {files.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: B.night, marginBottom: 8 }}>
                      Selected ({files.length}/{MAX_FILES})
                    </p>
                    {files.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#f9f9f9', marginBottom: 4 }}>
                        {f.preview ? (
                          <video src={f.preview} style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 48, height: 48, borderRadius: 6, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎬</div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600, color: B.night, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</p>
                          <p style={{ fontSize: 10, color: B.muted, margin: 0 }}>{(f.size / 1024 / 1024).toFixed(1)}MB</p>
                        </div>
                        <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 14 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={() => { if (files.length === 0) { setError('Add at least one video'); return; } setError(''); setStep(2); }}
                  style={{ width: '100%', padding: '13px 24px', borderRadius: 12, border: 'none', background: files.length > 0 ? B.forest : '#ccc', color: 'white', cursor: files.length > 0 ? 'pointer' : 'not-allowed', fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700 }}>
                  Next → Settings ({files.length} videos)
                </button>
              </>
            ) : (
              <>
                <QualitySettings
                  value={qualitySettings}
                  onChange={setQualitySettings}
                />

                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: B.night, marginBottom: 8 }}>
                    Video Details
                  </p>
                  {files.map((f, i) => (
                    <div key={i} style={{ padding: '8px 12px', borderRadius: 8, background: '#f9f9f9', marginBottom: 6 }}>
                      <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600, color: B.night, margin: '0 0 4px' }}>{f.name}</p>
                      <Input type="text" placeholder="Title" value={f.title}
                        onChange={e => updateFileMeta(i, 'title', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1.5px solid #e5e0d8', fontFamily: "'Montserrat',sans-serif", fontSize: 12, outline: 'none', boxSizing: 'border-box', marginBottom: 4 }} />
                      <Input type="text" placeholder="Destination (optional)" value={f.destination}
                        onChange={e => updateFileMeta(i, 'destination', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1.5px solid #e5e0d8', fontFamily: "'Montserrat',sans-serif", fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setStep(1)} style={{ flex: 1, padding: '13px 24px', borderRadius: 12, border: 'none', background: '#f5f5f5', color: B.night, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700 }}>← Back</button>
                  <button onClick={handleSubmit} disabled={creating || uploading}
                    style={{ flex: 2, padding: '13px 24px', borderRadius: 12, border: 'none', background: creating || uploading ? '#ccc' : B.forest, color: 'white', cursor: creating || uploading ? 'not-allowed' : 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700 }}>
                    {uploading ? `⏳ Uploading ${Object.values(uploadProgress).filter(p => p >= 100).length}/${files.length}...` : creating ? '⏳ Creating...' : '🚀 Upload All & Publish'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
